import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { CheckCircle2, Star } from 'lucide-react';
import Button from '../Button';
import { services } from '../data/services';
import StandorteSection from '../components/StandorteSection';
import {
  Section,
  Container,
  Badge,
  MotionCard,
  CardHeader,
  CardTitle,
  CardContent,
  AnimatedSection,
} from '../shared/ui';
import { imageProps, ImageName, SIZES } from '../shared/media';

const HeadBox = styled.div`
  text-align: center;
  padding: 2rem 0 1rem 0;
`;
const PageTitle = styled.h1`
  font-size: 2.4rem;
  font-weight: 800;
  color: var(--brand);
  margin-bottom: 1rem;
`;
const PageText = styled.p`
  max-width: 720px;
  margin: 0 auto;
  font-size: 1.12rem;
  line-height: 1.7;
  color: #3a4662;
`;
const Grid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  margin-top: 2.5rem;
  @media (min-width: 600px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;
const IconTitle = styled(CardTitle)`
  gap: 0.5rem;
  align-items: center;
  display: flex;
`;
const SubList = styled.ul`
  list-style: none;
  margin: 0.75rem 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const SubItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #3a4662;
  font-size: 0.98rem;
`;
const MoreLink = styled(Link)`
  display: inline-block;
  margin-top: 1rem;
  color: var(--brand);
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;
const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;
const ServiceCard = styled(MotionCard)`
  overflow: hidden;
`;
const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: linear-gradient(135deg, var(--brand) 0%, #234be6 55%, #6f8df5 100%);
`;
const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  ${ServiceCard}:hover & {
    transform: scale(1.08);
  }
`;
const ImageBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(255, 255, 255, 0.92);
  color: var(--brand);
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.3em 0.8em;
  border-radius: 999px;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 52, 196, 0.2);
`;
const CardImageFallback: React.FC<{ name: ImageName; alt: string }> = ({ name, alt }) => {
  const [failed, setFailed] = React.useState(false);
  if (failed) return null;
  return <CardImage {...imageProps(name, SIZES.card)} alt={alt} onError={() => setFailed(true)} />;
};
const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
`;
const CardPrice = styled.div`
  display: block;
  font-weight: 700;
  color: var(--brand);
  font-size: 0.95rem;
  margin-bottom: 0.6rem;
`;

const Leistungen: React.FC = () => (
  <>
    <Seo
      title="Unsere Leistungen | AP Clean Services Bielefeld & Gütersloh"
      description="Alle Reinigungsleistungen von AP Clean Services im Überblick: Praxisreinigung, Büroreinigung, Treppenhaus, Glas, Fitnessstudio, Kitas und Schulen sowie Sonder- & Anlassreinigung."
      path="/leistungen"
    />
    <Section>
      <Container>
        <HeadBox>
          <Badge>Leistungsübersicht</Badge>
          <PageTitle>Unsere Leistungen</PageTitle>
          <PageText>
            Für jede Objektart die passende Lösung – individuell nach Bedarf, Objekt und Anlass. Unser Schwerpunkt liegt auf der spezialisierten Praxisreinigung. Wählen Sie einen Bereich für weitere Informationen.
          </PageText>
        </HeadBox>
        <AnimatedSection once>
          <Grid as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
          >
            {services.map((s) => (
              <ServiceCard
                key={s.slug}
                variants={{ hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } }}
                whileHover={{ y: -6, boxShadow: "0 14px 32px rgba(0, 52, 196, 0.2)" }}
              >
                <CardLink to={`/${s.slug}`}>
                  <ImageWrap>
                    <CardImageFallback name={s.image} alt={s.title} />
                    {s.pakete && <ImageBadge><Star style={{ height: 12, width: 12 }} />Schwerpunkt</ImageBadge>}
                  </ImageWrap>
                  <CardHeader>
                    <IconTitle>
                      <s.icon style={{ height: 20, width: 20 }} />
                      {s.title}
                    </IconTitle>
                  </CardHeader>
                  <CardContent style={{ color: '#3a4662' }}>
                    {s.price && <CardPrice>{s.price}</CardPrice>}
                    {s.shortDesc}
                    <SubList>
                      {s.items.map((item, j) => (
                        <SubItem key={j}>
                          <CheckCircle2 style={{ height: 18, width: 18, color: 'var(--brand)', flexShrink: 0, marginTop: 2 }} />
                          <span>{item}</span>
                        </SubItem>
                      ))}
                    </SubList>
                    <MoreLink to={`/${s.slug}`}>Mehr erfahren →</MoreLink>
                  </CardContent>
                </CardLink>
              </ServiceCard>
            ))}
          </Grid>
        </AnimatedSection>
        <StandorteSection />
        <CtaRow>
          <Link to="/kontakt">
            <Button size="lg">Kostenlose Besichtigung anfragen</Button>
          </Link>
        </CtaRow>
      </Container>
    </Section>
  </>
);

export default Leistungen;
